import { GraphQLError } from "graphql";
import isEmpty from "is-empty";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { renewPasswordQuery } from "rsl-api-106/lib/queries";
import { Query, QueryRenewPasswordArgs } from "rsl-api-106/lib/types";
import { AlertType } from "types";

import { useApolloClient } from "@apollo/client";
import Alert from "@components/_Layout/Alert";
import Button from "@components/_Layout/Controls/Button";
import InputText from "@components/_Layout/Controls/InputText";
import { useSession } from "@hooks";

type ForgetPasswordProps = {
  defaultEmail: string;
};

const languagesVarsCodes = [
  "LANG_neobooking_forgot_password_sent", //Réinitialisation du mot de passe envoyé à votre adresse email.
  "LANG_neobooking_send", //Envoyer
  "LANG_neobooking_sending_in_progress", //Envoi en cours
  "LANG_neobooking_email_address", //Adresse email
  "LANG_neobooking_forgot_password_intro", //Si vous avez oublié votre mot de passe, renseignez votre adresse électronique et nous enverrons un email de réinitialisation de mot de passe.
  "LANG_neobooking_forgot_password", //Mot de passe oublié?
];

const ForgetPassword: React.FC<ForgetPasswordProps> = ({ defaultEmail }) => {
  /* Hooks */
  const client = useApolloClient();
  const { sessionId, getLanguageVarValue } = useSession();

  /* State */
  const [email, setEmail] = useState<string>(defaultEmail);

  /* Loading & Error State */
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [alert, setAlert] = useState<AlertType | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (sessionId) {
      setIsSubmitting(true);
      client
        .query<Query, QueryRenewPasswordArgs>({
          query: renewPasswordQuery(),
          variables: {
            session: { name: sessionId },
            input: {
              mail: email,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data.renewPassword?.renewPasswordDemand) {
            setAlert({
              variant: "primary",
              message: getLanguageVarValue(
                "LANG_neobooking_forgot_password_sent"
              ),
            });
          }
        })
        .catch((err: GraphQLError) => {
          setAlert({ variant: "danger", message: err.message });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  return (
    <div className="tw-flex">
      <div className="tw-w-full">
        {alert && (
          <div className="tw-mb-4">
            <Alert variant={alert.variant} dismissable={false}>
              {alert.message}
            </Alert>
          </div>
        )}
        <h3 className="tw-titre-principal-dosis tw-text-center tw-font-bold tw-uppercase tw-mb-4">
          {getLanguageVarValue("LANG_neobooking_forgot_password")}
        </h3>
        <p className="tw-text-center">
          {getLanguageVarValue("LANG_neobooking_forgot_password_intro")}
        </p>
        <form onSubmit={handleSubmit} onChange={() => setAlert(null)}>
          <div className="tw-my-4">
            <div className="tw-grid tw-grid-cols-1 tw-gap-2">
              <InputText
                placeholder={getLanguageVarValue(
                  "LANG_neobooking_email_address"
                )}
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Button
              variant="gradient"
              type="submit"
              additionalClassname="tw-w-[100%]"
              isLoading={isSubmitting}
              loadingChildren={
                <>
                  {getLanguageVarValue("LANG_neobooking_sending_in_progress")}
                  ...
                </>
              }
            >
              {getLanguageVarValue("LANG_neobooking_send")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const ForgetPassword2: React.FC<ForgetPasswordProps> = ({
  defaultEmail,
}) => {
  /* Hooks */
  const client = useApolloClient();
  const { sessionId, getLanguageVarValue } = useSession();

  /* State */
  const [email, setEmail] = useState<string>(defaultEmail);

  /* Loading & Error State */
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [alert, setAlert] = useState<AlertType | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (sessionId) {
      setIsSubmitting(true);
      client
        .query<Query, QueryRenewPasswordArgs>({
          query: renewPasswordQuery(),
          variables: {
            session: { name: sessionId },
            input: {
              mail: email,
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data.renewPassword?.renewPasswordDemand) {
            setAlert({
              variant: "primary",
              message: getLanguageVarValue(
                "LANG_neobooking_forgot_password_sent"
              ),
            });
          }
        })
        .catch((err: GraphQLError) => {
          setAlert({ variant: "danger", message: err.message });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  return (
    <div className="tw-container tw-mx-auto">
      <div className="tw-flex tw-rounded-xl tw-py-4 tw-px-6 tw-bg-white">
        <div className="tw-w-full">
          {alert && (
            <div className="tw-mb-4">
              <Alert variant={alert.variant} dismissable={false}>
                {alert.message}
              </Alert>
            </div>
          )}
          <p className="tw-text-center">
            {getLanguageVarValue("LANG_neobooking_forgot_password_intro")}
          </p>
          <form onSubmit={handleSubmit} onChange={() => setAlert(null)}>
            <div className="tw-my-4">
              <div className="tw-grid tw-grid-cols-1 tw-gap-2">
                <InputText
                  placeholder={getLanguageVarValue(
                    "LANG_neobooking_email_address"
                  )}
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="tw-flex tw-justify-end">
              <Button
                variant="gradient"
                type="submit"
                disabled={isEmpty(email)}
                additionalClassname="tw-w-[100%]"
                isLoading={isSubmitting}
                loadingChildren={
                  <>
                    {getLanguageVarValue("LANG_neobooking_sending_in_progress")}
                    ...
                  </>
                }
              >
                {getLanguageVarValue("LANG_neobooking_send")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

ForgetPassword.propTypes = {
  //
};

export default ForgetPassword;
